import { useCallback, useMemo, useState } from "react"
import { useQueryWithEndpoint, useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  GetAlertTemplatesForAssetDocument,
  EnableAlertRuleDocument,
  DisableAlertRuleDocument,
  UpdateAlertThresholdDocument,
  ResetAlertToDefaultsDocument,
  CreateCustomAlertDocument,
  UpdateCustomAlertDocument,
  DeleteCustomAlertDocument,
} from "@hydrologiq/gql-experience/graphql"
import type {
  AlertConditionGroup,
  AlertRuleState,
  AlertTemplate,
  AlertAssignment,
} from "@hydrologiq/shared/components/alerts"
import { buildAlertRules } from "@hydrologiq/shared/components/alerts/alertRules"

/** A new Custom Alert: its name and its conditions (one condition is a group of one). */
export interface CustomAlertDraft {
  name: string
  conditionGroup: AlertConditionGroup
}

/** A rejected Custom Alert: `field` names the input at fault, when the server says. */
export interface CustomAlertError {
  field?: string
  message: string
}

/** One of this Asset's Modules, whose alerts are listed with its own. */
export interface AlertModule {
  id: string
  name: string
  class?: string | null
}

export interface UseAssetAlertConfigurationReturn {
  /** One per alert and the Asset it judges: this Asset or one of its Modules */
  rules: AlertRuleState[]
  modules: AlertModule[]
  loading: boolean
  error: string | undefined
  /** `targetAssetId` defaults to this Asset; a rule on a Module passes the Module */
  enableRule: (templateId: string, targetAssetId?: string | null) => Promise<void>
  createCustomAlert: (draft: CustomAlertDraft) => Promise<CustomAlertError | null>
  updateCustomAlert: (templateId: string, draft: CustomAlertDraft) => Promise<CustomAlertError | null>
  deleteCustomAlert: (templateId: string) => Promise<void>
  disableRule: (assignmentId: string) => Promise<void>
  updateThreshold: (
    templateId: string,
    thresholdOverride: number,
    assignmentId?: string | null,
    targetAssetId?: string | null,
  ) => Promise<void>
  updateTimeWindow: (
    templateId: string,
    timeWindowOverrideSeconds: number,
    assignmentId?: string | null,
    targetAssetId?: string | null,
  ) => Promise<void>
  resetToDefaults: (assignmentId: string) => Promise<void>
  enabling: boolean
  disabling: boolean
  updating: boolean
  resetting: boolean
}

export function useAssetAlertConfiguration(assetId: string): UseAssetAlertConfigurationReturn {
  const [enabling, setEnabling] = useState(false)
  const [disabling, setDisabling] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [resetting, setResetting] = useState(false)

  const { data, loading, error, refetch } = useQueryWithEndpoint(
    GetAlertTemplatesForAssetDocument,
    {
      variables: { assetId },
      skip: !assetId,
      fetchPolicy: "cache-and-network",
    },
    "primary",
  )

  const [enableAlertRuleMutation] = useMutationWithEndpoint(
    EnableAlertRuleDocument,
    undefined,
    "primary",
  )

  const [disableAlertRuleMutation] = useMutationWithEndpoint(
    DisableAlertRuleDocument,
    undefined,
    "primary",
  )

  const [updateAlertThresholdMutation] = useMutationWithEndpoint(
    UpdateAlertThresholdDocument,
    undefined,
    "primary",
  )

  const [resetAlertToDefaultsMutation] = useMutationWithEndpoint(
    ResetAlertToDefaultsDocument,
    undefined,
    "primary",
  )

  const modules: AlertModule[] = useMemo(() => data?.alertTemplatesForAsset?.modules ?? [], [data])

  const rules: AlertRuleState[] = useMemo(() => {
    const templates = (data?.alertTemplatesForAsset?.templates ?? []) as AlertTemplate[]
    const assignments = (data?.alertTemplatesForAsset?.assignments ?? []) as AlertAssignment[]
    const asset = data?.alertTemplatesForAsset?.asset
    return buildAlertRules(templates, assignments, [{ id: assetId, class: asset?.class }, ...modules])
  }, [data, assetId, modules])

  const [createCustomAlertMutation] = useMutationWithEndpoint(
    CreateCustomAlertDocument,
    undefined,
    "primary",
  )

  const [updateCustomAlertMutation] = useMutationWithEndpoint(
    UpdateCustomAlertDocument,
    undefined,
    "primary",
  )

  const [deleteCustomAlertMutation] = useMutationWithEndpoint(
    DeleteCustomAlertDocument,
    undefined,
    "primary",
  )

  const enableRule = useCallback(
    async (templateId: string, targetAssetId?: string | null) => {
      setEnabling(true)
      try {
        const input = { templateId, assetId: targetAssetId ?? assetId }
        const { errors } = await enableAlertRuleMutation({ variables: { input } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setEnabling(false)
      }
    },
    [assetId, enableAlertRuleMutation, refetch],
  )

  const disableRule = useCallback(
    async (assignmentId: string) => {
      setDisabling(true)
      try {
        const { errors } = await disableAlertRuleMutation({ variables: { assignmentId } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setDisabling(false)
      }
    },
    [disableAlertRuleMutation, refetch],
  )

  const updateThreshold = useCallback(
    async (templateId: string, thresholdOverride: number, assignmentId?: string | null, targetAssetId?: string | null) => {
      setUpdating(true)
      try {
        if (assignmentId) {
          const input = { assignmentId, thresholdOverride }
          const { errors } = await updateAlertThresholdMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        } else {
          const input = { templateId, assetId: targetAssetId ?? assetId, thresholdOverride, enabled: false }
          const { errors } = await enableAlertRuleMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        }
        await refetch()
      } finally {
        setUpdating(false)
      }
    },
    [assetId, enableAlertRuleMutation, updateAlertThresholdMutation, refetch],
  )

  const updateTimeWindow = useCallback(
    async (templateId: string, timeWindowOverrideSeconds: number, assignmentId?: string | null, targetAssetId?: string | null) => {
      setUpdating(true)
      try {
        if (assignmentId) {
          const input = { assignmentId, timeWindowOverrideSeconds }
          const { errors } = await updateAlertThresholdMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        } else {
          const input = { templateId, assetId: targetAssetId ?? assetId, timeWindowOverrideSeconds, enabled: false }
          const { errors } = await enableAlertRuleMutation({ variables: { input } })
          if (errors?.length) throw new Error(errors[0].message)
        }
        await refetch()
      } finally {
        setUpdating(false)
      }
    },
    [assetId, enableAlertRuleMutation, updateAlertThresholdMutation, refetch],
  )

  const resetToDefaults = useCallback(
    async (assignmentId: string) => {
      setResetting(true)
      try {
        const { errors } = await resetAlertToDefaultsMutation({ variables: { assignmentId } })
        if (errors?.length) throw new Error(errors[0].message)
        await refetch()
      } finally {
        setResetting(false)
      }
    },
    [resetAlertToDefaultsMutation, refetch],
  )

  /** Runs a Custom Alert save, returning the server's rejection rather than throwing it. */
  const saveCustomAlert = useCallback(
    async (save: () => Promise<{ errors?: readonly unknown[] }>): Promise<CustomAlertError | null> => {
      try {
        const { errors } = await save()
        if (errors?.length) return toCustomAlertError(errors[0])
        await refetch()
        return null
      } catch (e) {
        // Apollo's default error policy rejects with the GraphQL errors attached
        const graphQLError = (e as { graphQLErrors?: unknown[] })?.graphQLErrors?.[0]
        if (graphQLError) return toCustomAlertError(graphQLError)
        return { message: "We couldn't save the alert. Please try again." }
      }
    },
    [refetch],
  )

  const createCustomAlert = useCallback(
    (draft: CustomAlertDraft) => saveCustomAlert(() => createCustomAlertMutation({ variables: { assetId, input: draft } })),
    [assetId, createCustomAlertMutation, saveCustomAlert],
  )

  const updateCustomAlert = useCallback(
    (templateId: string, draft: CustomAlertDraft) =>
      saveCustomAlert(() => updateCustomAlertMutation({ variables: { templateId, input: draft } })),
    [updateCustomAlertMutation, saveCustomAlert],
  )

  const deleteCustomAlert = useCallback(
    async (templateId: string) => {
      const { errors } = await deleteCustomAlertMutation({ variables: { templateId } })
      if (errors?.length) throw new Error(errors[0].message)
      await refetch()
    },
    [deleteCustomAlertMutation, refetch],
  )

  return {
    rules,
    modules,
    loading,
    error: error?.message,
    enableRule,
    createCustomAlert,
    updateCustomAlert,
    deleteCustomAlert,
    disableRule,
    updateThreshold,
    updateTimeWindow,
    resetToDefaults,
    enabling,
    disabling,
    updating,
    resetting,
  }
}

function toCustomAlertError(error: unknown): CustomAlertError {
  const { message, extensions } = (error ?? {}) as {
    message?: string
    extensions?: { field?: unknown }
  }
  return {
    field: typeof extensions?.field === "string" ? extensions.field : undefined,
    message: message || "We couldn't save the alert. Please try again.",
  }
}
